import type { SupportedStateCode } from '@/common/constants/states';
import type { ContactsRequiredFields } from '../shared/schema';

/**
 * Per-state behavior of the Listing contacts form. Mirrors the LOM
 * `formConfig` pattern (a flat `DEFAULT_CONFIG` plus per-state deltas). Knobs
 * are derived from the legacy cover sheet listing contact overrides in
 * `views/Coversheet/configurations/listing/overrides/*`.
 *
 * For a listing the user represents the SELLER, so the own-side `agent` is the
 * seller's agent and `client` is the seller; the counterparty side is the
 * buyer. Own-side attorney → `attorney`; counterparty attorney →
 * `counterpartyAttorney`.
 */
export interface ListingContactsConfig {
  // Seller (own) side
  showSellersAttorney: boolean;
  sellerAgentLicenseDisabled: boolean;
  showSellerAgentMlsCode: boolean;
  showSellerIntermediaryBanner: boolean;

  // Buyer (counterparty) side
  showBuyersAttorney: boolean;
  showBuyerAgentMlsCode: boolean;
  showBuyerIntermediaryBanner: boolean;

  // Additional contacts
  showTitleOfficer: boolean;
  showEscrowOfficer: boolean;
  showAttorneySection: boolean;
  showClosingAttorneySection: boolean;
  showPropertyManagingAgentSection: boolean;
}

const DEFAULT_CONFIG: ListingContactsConfig = {
  showSellersAttorney: false,
  sellerAgentLicenseDisabled: false,
  showSellerAgentMlsCode: false,
  showSellerIntermediaryBanner: false,

  showBuyersAttorney: false,
  showBuyerAgentMlsCode: false,
  showBuyerIntermediaryBanner: false,

  showTitleOfficer: true,
  showEscrowOfficer: false,
  showAttorneySection: true,
  showClosingAttorneySection: false,
  showPropertyManagingAgentSection: false,
};

export type ListingContactsConfigDelta = Partial<ListingContactsConfig>;

const STATE_CONFIGS: Partial<
  Record<SupportedStateCode, ListingContactsConfigDelta>
> = {
  CA: { showEscrowOfficer: true },
  GA: {
    showSellerAgentMlsCode: true,
    showBuyerAgentMlsCode: true,
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  MA: {
    showSellersAttorney: true,
    showBuyersAttorney: true,
    showTitleOfficer: false,
    showAttorneySection: false,
  },
  NC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  NJ: {
    showBuyersAttorney: true,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
  },
  NY: {
    showSellersAttorney: true,
    showBuyersAttorney: true,
    sellerAgentLicenseDisabled: true,
    showTitleOfficer: false,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
  },
  OR: { showTitleOfficer: false, showEscrowOfficer: true },
  SC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  TX: {
    showSellerIntermediaryBanner: true,
    showBuyerIntermediaryBanner: true,
  },
  WA: {
    showEscrowOfficer: true,
    showSellerAgentMlsCode: true,
    showBuyerAgentMlsCode: true,
  },
};

export const buildListingContactsConfig = (
  delta: ListingContactsConfigDelta = {},
): ListingContactsConfig => ({ ...DEFAULT_CONFIG, ...delta });

export const resolveListingContactsConfig = (
  state: SupportedStateCode,
): ListingContactsConfig => buildListingContactsConfig(STATE_CONFIGS[state]);

/**
 * The listing contacts' required name/email rules. Single source shared by the
 * validation schema (sidebar badge + lockdown) and the form's asterisk/
 * highlight. The user represents the seller, so the seller's agent + seller are
 * required; the buyer (counterparty) side is not. Listings have no per-state
 * required-ness differences, so this is a constant.
 */
export const listingContactRequiredFields: ContactsRequiredFields = {
  agent: { firstName: true, lastName: true, email: true },
  counterpartyAgent: { firstName: false, lastName: false, email: false },
  client: { firstName: true, lastName: true, email: true },
  counterpartyClient: { firstName: false, lastName: false, email: false },
};
